import WarningIcon from '@mui/icons-material/Warning';
import { AppBar, Box, Toolbar } from '@mui/material';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { IDialogFor } from '../../types';
import philipsLogo from '../assets/images/png/logo.png';
import { ECommonDialogType, EFeatureStatus, EPageName } from '../data/Constants';
import { AppDispatch, AppState } from '../store/AppStore';
import { setResetDataFlag } from '../store/states/procos';
import ConfirmationDialog from './ConfirmationDialog';
import HeaderLogin from './HeaderLogin';

//Render the InActive Product Warning Message
const renderInActiveWarning = (productStatus: string, t): ReactElement => {
  return (
    <div className="rowFlex inActiveWarningMessage">
      {productStatus !== EFeatureStatus.Active && (
        <>
          <span className="colFlex warningIcon">
            <WarningIcon className="warningSize" />
          </span>
          &nbsp;
          <div className="warningMessage">
            <h2 className="m-0"> {t('messages.inActiveProduct.header')}</h2>
            <span>{t('messages.inActiveProduct.message')}</span>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Renders the header which icludes the logo and user info
 * @returns {JSX.Element} the header component
 */
const Header = (): ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [confirmWarning, setConfirmWarning] = useState({
    open: false,
    title: 'dialogBox.unsavedChanges',
    description: 'messages.cancel.homeWarning',
    alertFor: ECommonDialogType.HomeClickWarning,
  });

  const productStatus: string = useSelector((state: AppState) => state.procos.productStatus);
  const isDataUpdated = useSelector((state: AppState) => {
    if (
      state.procos.isDataUpdated ||
      state.pdm.isModified ||
      state.category.isCategoryDataModified
    ) {
      return true;
    } else {
      return false;
    }
  });

  //If user click on the Philips logo in header ,user will navigate to the HomePage
  const navigateToHome = (): void => {
    if (isDataUpdated) {
      setConfirmWarning({ ...confirmWarning, open: true });
    } else {
      dispatch(setResetDataFlag(true));
    }
  };

  //This method triggers when user clicks on cancel or ok button on confirm dialog
  const onConfirmStatus = (confirm: IDialogFor): void => {
    setConfirmWarning({ ...confirmWarning, open: false });
    if (confirm.status) {
      dispatch(setResetDataFlag(true));
    }
  };

  return (
    <>
      <AppBar className="headerStyle root" position="static">
        <Toolbar className="toolbar">
          <Box className="toolbarSection">
            {[EPageName.Home].includes(location.pathname) ? (
              <img
                className="zoom80"
                src={philipsLogo}
                data-testid="home-image"
                alt="philips-logo"
              />
            ) : (
              <button
                className="headerLogoButton"
                data-testid="header-logo-button"
                onClick={navigateToHome}
                aria-label="Go to Home"
              >
                <img className="zoom80 headerLogo" src={philipsLogo} alt="Philips Logo" />
              </button>
            )}
          </Box>
          {renderInActiveWarning(productStatus, t)}
          <Box className="toolbarSection">
            <HeaderLogin />
          </Box>
        </Toolbar>
      </AppBar>
      {confirmWarning.open && (
        <ConfirmationDialog dialogInfo={confirmWarning} onConfirmAction={onConfirmStatus} />
      )}
    </>
  );
};

export default Header;
